import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as Calender } from '../../../../assets/icons/calender.svg';
import { ReactComponent as Refresh } from '../../../../assets/icons/refresh-fill-icon.svg';
import DefaultApiLimit from '../../../../config/defaultLimit';
import {
  SoftWareLogsInterFace,
  SoftWareLogsResponseInterface,
} from '../../../../interface/soft-ware-logs-interface';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { setNotificationMessage } from '../../../../redux/slices/NotificationManagementSlice';
import {
  disableSoftWareLogsFiltersApplied,
  fetchListSoftWareLogsFilters,
  fetchSoftWareLogs,
  updateSoftWareLogsFilters,
  setSortData,
} from '../../../../redux/slices/SoftWareLogsSlice';
import MyButton from '../../../../shared-components/button/Button';
import CustomTable from '../../../../shared-components/custom-table/CustomTable';
import { GetParametersFromCommonFilters } from '../../../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../../../shared-functions/ConvertUTCtoDate';
import ConvertUTCtoHHMM from '../../../../shared-functions/ConvertUTCtoHHMM';
import { exportCSV, exportPDF } from '../../../../shared-functions/ExportFile';
import FormatUtcString from '../../../../shared-functions/FormatUtcString';
import stationManagementApi from '../../../../utils/api/station-management';

/**
 * SoftWare Logs
 * @returns {any} The rendered component
 */
function SoftWareLogList() {
  const { t } = useTranslation('sidePanel');
  const dispatch = useAppDispatch();
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const { selectedChargingPoint } = useAppSelector((state) => state.stationManagement);
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const {
    SoftWareLogs,
    limit,
    page,
    totalPages,
    totalResults,
    loading,
    filterListLoading,
    filters,
    isFilterUpdated,
    sortBy,
    sortField,
  } = useAppSelector((state) => state.softWareLogs);
  const { selectedSingleStationPerformanceList } = useAppSelector(
    (state) => state.stationManagement,
  );

  const [lastMins, setLastMins] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<string>('');

  const column = [
    {
      label: t('date'),
      accessorKey: 'created_at' as keyof SoftWareLogsInterFace,
      sortAllow: true,

      /**
       * Converting timestamp to UTC date
       * @param {any} stationActivity The activity object
       * @returns {any} The formatted date
       */
      customData: (stationActivity: SoftWareLogsInterFace) => (
        <p>{ConvertUTCtoDate(stationActivity.created_at)}</p>
      ),
    },
    {
      label: t('time'),
      accessorKey: 'time' as keyof SoftWareLogsInterFace,
      sortAllow: true,
      /**
       * Converting timestamp to UTC time
       * @param {any} stationActivityTime The activity object
       * @returns {any} The formatted time
       */
      customData: (stationActivityTime: SoftWareLogsInterFace) => (
        <p>{FormatUtcString(ConvertUTCtoHHMM(stationActivityTime.created_at))}</p>
      ),
    },
    {
      label: t('type'),
      accessorKey: 'type' as keyof SoftWareLogsInterFace,
      sortAllow: false,

      /**
       * Converting timestamp to UTC time
       * @param {any} stationActivity The activity object
       * @returns {any} The formatted type
       */
      customData: (stationActivity: SoftWareLogsInterFace) => {
        const type = stationActivity.type?.toLowerCase();

        return (
          <p className={`${type === 'in' ? 'type-in' : 'type-out'}`}>{type ? t(type) : '-'}</p>
        );
      },
    },
    {
      label: t('messageType'),
      accessorKey: 'action' as keyof SoftWareLogsInterFace,
    },
    {
      label: t('message'),
      accessorKey: 'message' as keyof SoftWareLogsInterFace,
      columnWidth: 500,
    },
  ];

  /**
   * Handle the fetch the data in api function
   * @param {any} data The request data
   */
  const fetchData = (data: SoftWareLogsResponseInterface) => {
    if (!loading) {
      dispatch(
        fetchSoftWareLogs({
          ...data,
          chargingPointSn: selectedSingleStationPerformanceList?.id,
          chargingStation: selectedChargingPoint?.id,
        }),
      );
      const now = new Date();
      const hours = now.getHours();
      const minutes = now.getMinutes();
      const ampm = hours >= 12 ? 'PM' : 'AM';
      const formattedHours = hours % 12 || 12;
      const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes;
      setLastUpdated(`${formattedHours}:${formattedMinutes} ${ampm}`);
    }
  };

  /**
   * Current columns for export
   */
  const currentColumns = [
    { label: t('sNo'), accessorKey: '__serial__' },
    { label: t('date'), accessorKey: 'created_at' },
    { label: t('time'), accessorKey: 'time' },
    { label: t('type'), accessorKey: 'type' },
    { label: t('actionType'), accessorKey: 'action' },
    { label: t('message'), accessorKey: 'message' },
  ];

  /**
   * Filtering columns for export without hidden columns
   */
  const exportCurrentColumns = currentColumns.filter(
    (col) => col.accessorKey === '__serial__' || !hiddenColumns.includes(col.accessorKey),
  );

  /**
   * Export data function
   * @param {any} type - The type
   */
  const exportDataPdfOrCSV = (type: string) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    setExportLoader(true);
    stationManagementApi
      .fetchStationActivityExport({
        ...queryParameters,
        sortBy,
        sortField,
        chargingStation: selectedChargingPoint?.id,
        chargingPointSn: selectedSingleStationPerformanceList?.id,
      })
      .then((response) => {
        // Convert data to CSV format
        const headers = exportCurrentColumns.map((col) => col.label);
        const rows = response.data.map((row: SoftWareLogsInterFace, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return index + 1;
              case 'created_at':
                return ConvertUTCtoDate(row?.created_at) || t('unavailable');
              case 'time':
                return ConvertUTCtoHHMM(row?.created_at) || t('unavailable');
              default:
                return (row as any)[col?.accessorKey] || t('unavailable');
            }
          }),
        );
        if (type === 'csv') {
          exportCSV(headers, rows, 'Station Activity.csv');
        } else {
          exportPDF(headers, rows, 'Station Activity.pdf');
        }
        setExportLoader(false);
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message || t('somethingWentWrong'),
            status: true,
            type: 'error',
            code: error?.response?.data?.code,
          }),
        );
      });
  };

  /**
   * Function for sorting table data
   * @param {object} sortData The sort configuration
   * @param {any} sortData.sortBy - The sortBy prop
   * @param {any} sortData.sortField - The sortField prop
   */
  const handleSortFunction = (sortData: { sortBy: 'asc' | 'desc'; sortField: string }) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSortData(sortData));
    fetchData({
      ...queryParameters,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };
  /**
   * Table pagination function
   * @param {any} value The pagination value
   */
  const pageNateLimitChange = (value: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...value,
      ...queryParameters,
      sortBy,
      sortField,
    });
  };

  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({ ...queryParameters, page, limit, sortBy, sortField });
  };

  useEffect(() => {
    if (isFilterUpdated) {
      const queryParameters = GetParametersFromCommonFilters(filters);
      fetchData({
        ...queryParameters,
        page,
        limit,
      });
      dispatch(disableSoftWareLogsFiltersApplied());
    }
  }, [filters, selectedSingleStationPerformanceList]);

  useEffect(() => {
    if (!selectedSingleStationPerformanceList?.id) return;

    const currentId = selectedSingleStationPerformanceList.id;
    const lastId = sessionStorage.getItem('SoftWareLogsFiltersList'); // string | null

    // If same id as last time -> do nothing
    if (lastId !== currentId || filters.length === 1) {
      dispatch(
        fetchListSoftWareLogsFilters({
          chargingStation: selectedChargingPoint?.id,
          chargingPointSn: selectedSingleStationPerformanceList?.id,
        }),
      );
    }
    if (SoftWareLogs.length === 0 || lastId !== currentId) {
      const queryParameters = GetParametersFromCommonFilters(filters);
      fetchData({
        ...queryParameters,
        page,
        limit,
      });
      dispatch(disableSoftWareLogsFiltersApplied());
    }
    // Save this id so next time we can compare
    sessionStorage.setItem('SoftWareLogsFiltersList', currentId);
  }, [selectedChargingPoint?.id, filters, selectedSingleStationPerformanceList]);

  return (
    <div className="soft-ware-logs-list">
      <CustomTable
        version="v2"
        header={column}
        data={SoftWareLogs}
        serialNo
        sizeChangeButtonRequired
        limit={limit}
        page={page}
        totalPages={totalPages}
        totalResults={totalResults}
        loading={loading}
        filterListLoading={filterListLoading}
        filters={filters}
        filtersToDispatch={updateSoftWareLogsFilters}
        filterListToDispatch={fetchListSoftWareLogsFilters}
        exportDataPdfOrCSV={exportDataPdfOrCSV}
        exportLoader={exportLoader}
        pageLimitChange={pageNateLimitChange}
        refershCurrentPages={handleRefresh}
        height={400}
        rightTopContent={
          <div className="top-right-content">
            <div className="last-15-mins-button">
              <MyButton
                label={t('last15Mins')}
                buttonType={lastMins ? 'primary-outline-none' : 'none'}
                svgIcon={<Calender />}
                onClickFunc={() => {
                  if (selectedChargingPoint) {
                    const newLastMins = !lastMins;
                    setLastMins(newLastMins);
                    const queryParameters = GetParametersFromCommonFilters(filters);
                    fetchData({
                      page: DefaultApiLimit.page,
                      limit,
                      ...queryParameters,
                      last15min: newLastMins,
                    });
                  }
                }}
              />
            </div>
            <div className="auto-refreshed-time">
              <Refresh />
              <p>{t('lastUpdatedLabel', { time: lastUpdated })}</p>
            </div>
          </div>
        }
        sortData={{
          sortBy,
          sortField,
        }}
        handleSortChange={handleSortFunction}
        disableHeader={false}
        leftTopContent={<> </>}
        onHiddenColumnsChange={setHiddenColumns}
      />
    </div>
  );
}

export default SoftWareLogList;
